// ============================================================================
// RAID ITEM HOOKS
//
// One fetch per session, joined to artefacts client-side. Denormalising RAID
// items onto stories server-side was rejected: the requirements blob refetches
// wholesale, so every resolve would invalidate the entire payload, and an item
// linked to several artefacts would be duplicated across two sources of truth.
//
// Mutations patch the cache in place rather than invalidating, so resolving an
// item on a card does not cause a visible reload of the list underneath it.
// ============================================================================

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listSessionRaidItems, listProjectRaidItems, createSessionRaidItem, createProjectRaidItem,
  updateRaidItem, resolveRaidItem, linkRaidItem, unlinkRaidItem, deleteRaidItem,
  RaidItemInput, RaidFilters,
} from '../services/raid';
import { RaidItem, RaidItemsResponse, RaidLink, RaidLinkTargetType, RaidStatus } from '../types/raid';

type Scope = { sessionId?: string; projectId?: string };

export const raidQueryKey = (scope: Scope, filters?: RaidFilters) =>
  ['raid-items', scope.sessionId ?? `project:${scope.projectId}`, filters ?? {}] as const;

export function useRaidItems(scope: Scope, filters?: RaidFilters) {
  const enabled = !!(scope.sessionId || scope.projectId);
  return useQuery<RaidItemsResponse>({
    queryKey: raidQueryKey(scope, filters),
    queryFn: () => (scope.sessionId
      ? listSessionRaidItems(scope.sessionId, filters)
      : listProjectRaidItems(scope.projectId!, filters)),
    enabled,
    staleTime: 15000,
  });
}

/**
 * Index of artefact -> its RAID items.
 *
 * The key carries the session because session-level story ids collide across
 * personas; keying on id alone would attribute one session's blocker to an
 * unrelated story elsewhere in the project.
 */
export function raidTargetKey(targetType: string, targetId: string, sessionId?: string | null): string {
  return `${targetType}#${sessionId ?? ''}#${targetId}`;
}

export interface RaidIndexEntry {
  items: RaidItem[];
  openCount: number;
  blockingCount: number;
}

export function useRaidIndex(items: RaidItem[] | undefined): Map<string, RaidIndexEntry> {
  return useMemo(() => {
    const map = new Map<string, RaidIndexEntry>();
    for (const item of items ?? []) {
      for (const link of item.links ?? []) {
        const key = raidTargetKey(link.targetType, link.targetId, link.targetSessionId);
        const entry = map.get(key) ?? { items: [], openCount: 0, blockingCount: 0 };
        entry.items.push(item);
        if (item.status === 'open') {
          entry.openCount += 1;
          if (link.relation === 'blocks' || item.blockingSeverity === 'blocks') entry.blockingCount += 1;
        }
        map.set(key, entry);
      }
    }
    return map;
  }, [items]);
}

export function lookupRaid(
  index: Map<string, RaidIndexEntry>,
  targetType: RaidLinkTargetType,
  targetId: string,
  sessionId?: string | null
): RaidIndexEntry {
  return index.get(raidTargetKey(targetType, targetId, sessionId)) ?? { items: [], openCount: 0, blockingCount: 0 };
}

// ============================================================================
// Mutations
// ============================================================================

export function useRaidMutations(scope: Scope, filters?: RaidFilters) {
  const qc = useQueryClient();
  const key = raidQueryKey(scope, filters);

  /** Replace one item in place; add it if it is new. */
  const patchCache = (item: RaidItem) => {
    qc.setQueryData<RaidItemsResponse>(key, prev => {
      if (!prev) return prev;
      const exists = prev.raidItems.some(i => i.raidId === item.raidId);
      const raidItems = exists
        ? prev.raidItems.map(i => (i.raidId === item.raidId ? item : i))
        : [...prev.raidItems, item];
      return { ...prev, raidItems, count: raidItems.length, openCount: raidItems.filter(i => i.status === 'open').length };
    });
  };

  const dropFromCache = (raidId: string) => {
    qc.setQueryData<RaidItemsResponse>(key, prev => {
      if (!prev) return prev;
      const raidItems = prev.raidItems
        .filter(i => i.raidId !== raidId)
        // The server sweeps inbound links on delete; mirror that locally so the
        // UI does not briefly show a link to something that no longer exists.
        .map(i => ({
          ...i,
          links: (i.links ?? []).filter(l => !(l.targetType === 'raid_item' && l.targetId === raidId)),
        }));
      return { ...prev, raidItems, count: raidItems.length, openCount: raidItems.filter(i => i.status === 'open').length };
    });
  };

  const create = useMutation({
    mutationFn: (input: RaidItemInput) => (scope.sessionId
      ? createSessionRaidItem(scope.sessionId, input)
      : createProjectRaidItem(scope.projectId!, input)),
    onSuccess: patchCache,
  });

  const update = useMutation({
    mutationFn: ({ raidId, patch }: { raidId: string; patch: Partial<RaidItemInput> }) =>
      updateRaidItem(scope, raidId, patch),
    onSuccess: patchCache,
  });

  const resolve = useMutation({
    mutationFn: ({ raidId, status, resolution }: { raidId: string; status: RaidStatus; resolution?: string }) =>
      resolveRaidItem(scope, raidId, { status, resolution }),
    onSuccess: patchCache,
  });

  const link = useMutation({
    mutationFn: ({ raidId, link: l }: { raidId: string; link: RaidLink }) => linkRaidItem(scope, raidId, l),
    onSuccess: patchCache,
  });

  const unlink = useMutation({
    mutationFn: ({ raidId, link: l }: { raidId: string; link: RaidLink }) => unlinkRaidItem(scope, raidId, l),
    onSuccess: patchCache,
  });

  const remove = useMutation({
    mutationFn: (raidId: string) => deleteRaidItem(scope, raidId).then(() => raidId),
    onSuccess: dropFromCache,
  });

  return { create, update, resolve, link, unlink, remove };
}
